import { CartAddEvent } from '@theme/events';

/** 追加済み表示を出しておく時間（ms）。過ぎたら `#render()` で元の文言に戻す */
const ADDED_DURATION = 2000;

/**
 * PDP の「走行に必要なおすすめ商品」（バンドル購入UI・#207）。
 *
 * やること2つだけ:
 *   1. チェックの ON/OFF で「点数 ・ 合計金額」と CTA の文言を再計算する
 *   2. チェックした商品を `/cart/add.js` に複数 items でまとめて投入する
 *
 * 文言のテンプレートと区切り（`  ・  `）は Liquid 側の data 属性で持つ。
 * 初期表示は Liquid が同じ組み立てで出しているので、JS が来る前も正しい文言が出る。
 *
 * 点数は DOM から数える（`disabled` = 在庫切れ）。Liquid と JS で二重に状態を持つと
 * バリアント切替やセクション再レンダリングでズレるため、数える元は DOM だけにする。
 *
 * カート投入後は Horizon と同じ `CartAddEvent` を投げる。これでカートドロワーの
 * 自動オープン（`snippets/cart-drawer.liquid` の `auto-open`）とヘッダーのカート
 * バブル更新に乗れる（`assets/cart-drawer.js` / `assets/cart-icon.js` が document で待っている）。
 */
class RunningRequirementsYokomo extends HTMLElement {
  #abortController = new AbortController();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedTimer;

  connectedCallback() {
    this.meta = this.querySelector('[data-ref="meta"]');
    this.cta = this.querySelector('[data-ref="cta"]');
    this.note = this.querySelector('[data-ref="note"]');
    this.error = this.querySelector('[data-ref="error"]');
    this.checkboxes = Array.from(this.querySelectorAll('[data-ref="checkbox"]'));

    if (!this.checkboxes.length || !this.cta) return;

    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    this.cta.addEventListener('click', this.#handleBulkAdd, { signal });

    // Liquid が出した初期文言と DOM の状態が一致していることを保証する
    // （テーマエディタでの再描画や、ブラウザのフォーム状態復元で checked がズレる場合がある）。
    this.#render();
  }

  disconnectedCallback() {
    clearTimeout(this.#addedTimer);
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.dataset.ref !== 'checkbox') return;
    this.#render();
  };

  /** @returns {HTMLInputElement[]} 在庫があり、かつチェックされている行 */
  #selected() {
    return this.checkboxes.filter((checkbox) => !checkbox.disabled && checkbox.checked);
  }

  /** 点数・合計・CTA・注記をまとめて描き直す */
  #render() {
    // 追加済み表示の途中でチェックを変えたら、その場で通常の文言に戻す（タイマーも捨てる）
    clearTimeout(this.#addedTimer);
    delete this.cta.dataset.added;

    const total = this.checkboxes.length;
    const outOfStock = this.checkboxes.filter((checkbox) => checkbox.disabled).length;
    const inStock = total - outOfStock;
    const selected = this.#selected();
    const sum = selected.reduce((amount, checkbox) => amount + Number(checkbox.dataset.price || 0), 0);
    const money = this.#formatMoney(sum);
    const data = this.dataset;

    if (this.meta) {
      // 全部選択中は「8点」、一部だけなら「8点中3点選択」（Figma の N8Stockout / N5Partial）
      const countText =
        selected.length === inStock
          ? this.#fill(data.labelCount, { count: total })
          : this.#fill(data.labelPartial, { total, checked: selected.length });

      const parts = [countText, this.#fill(data.labelTotal, { price: money })];
      if (outOfStock > 0) parts.push(this.#fill(data.labelOutOfStock, { count: outOfStock }));

      this.meta.textContent = parts.join(data.labelSeparator || ' ');
    }

    this.cta.textContent = this.#fill(data.labelCta, { count: selected.length, price: money });
    this.cta.disabled = selected.length === 0;

    if (this.note) {
      // 在庫切れが無い商品では注記自体を出さない（Figma も在庫切れ有りのときだけ描いている）
      this.note.textContent = outOfStock > 0 ? this.#fill(data.labelNote, { count: outOfStock }) : '';
      this.note.hidden = outOfStock === 0;
    }
  }

  /**
   * 文言テンプレートの `{count}` `{total}` `{checked}` `{price}` を埋める。
   * テンプレートは Liquid の data 属性が持つ（日本語をこのファイルに書かないため）。
   *
   * @param {string | undefined} template
   * @param {Record<string, string | number>} values
   * @returns {string}
   */
  #fill(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
      template || ''
    );
  }

  #handleBulkAdd = async () => {
    const selected = this.#selected();
    if (!selected.length || !this.cta) return;

    const items = selected.map((checkbox) => ({ id: Number(checkbox.value), quantity: 1 }));

    // カートのセクションを一緒に取り直す（Horizon の product-form.js と同じ扱い）。
    // これが無いとドロワーの中身が古いまま開く。
    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    // 追加済み表示のまま再度押された場合は、通常の文言に戻してから投入する（前回のタイマーも捨てる）
    if (this.cta.dataset.added) this.#render();

    this.#setBusy(true);
    this.#hideError();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      const body = await response.json();

      // Cart API はエラーでも 4xx + JSON を返す。`status` が入っていたら失敗。
      if (!response.ok || body.status) {
        this.#showError(body.description || body.message);
        return;
      }

      // cart-icon.js は source が 'product-form-component' のときだけ itemCount を現在の点数に加算し、
      // それ以外は itemCount をカート合計として上書きする。追加した点数を渡すので加算側に乗せる。
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form-component',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: body.sections,
        })
      );

      this.#showAdded();
    } catch (error) {
      console.error(error);
      this.#showError();
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    if (!this.cta) return;
    this.cta.disabled = busy || this.#selected().length === 0;
    this.cta.setAttribute('aria-busy', String(busy));
  }

  /**
   * 投入成功を CTA の文言で示し、`ADDED_DURATION` 後に `#render()` で元の文言へ戻す。
   * `finally` の `#setBusy(false)` は `disabled` / `aria-busy` しか触らないので、この文言は上書きされない。
   */
  #showAdded() {
    if (!this.cta || !this.dataset.labelAdded) return;

    clearTimeout(this.#addedTimer);
    this.cta.dataset.added = 'true';
    this.cta.textContent = this.dataset.labelAdded;
    this.#addedTimer = setTimeout(() => this.#render(), ADDED_DURATION);
  }

  /**
   * 失敗を黙って落とさない。`role="status"` に文章で入れる（色だけで異常を示さない）。
   * @param {string} [message]
   */
  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message || this.dataset.labelError || '';
    this.error.hidden = false;
  }

  #hideError() {
    if (!this.error) return;
    this.error.textContent = '';
    this.error.hidden = true;
  }

  /**
   * `money` フィルタと同じ出力を JS で作る。テンプレートは Liquid から
   * `data-money-format`（= `shop.money_format`）で受け取る。
   *
   * このストアは JPY で `¥{{amount_no_decimals}}`。小数を持つ通貨に切り替わっても
   * 4種のプレースホルダを解釈するので出力は崩れない。
   *
   * @param {number} cents - 最小通貨単位の金額（Shopify の `variant.price`）
   * @returns {string}
   */
  #formatMoney(cents) {
    const template = this.dataset.moneyFormat || '{{amount}}';

    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
      if (placeholder === 'currency') return this.dataset.currency || '';
      if (!placeholder.startsWith('amount')) return match;

      const commaSeparator = placeholder.endsWith('with_comma_separator');
      const thousandsSeparator = commaSeparator ? '.' : ',';
      const decimalSeparator = commaSeparator ? ',' : '.';
      const precision = placeholder.startsWith('amount_no_decimals') ? 0 : 2;

      const [integer, fraction] = (cents / 100).toFixed(precision).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

      return fraction ? `${grouped}${decimalSeparator}${fraction}` : grouped;
    });
  }
}

if (!customElements.get('running-requirements-yokomo')) {
  customElements.define('running-requirements-yokomo', RunningRequirementsYokomo);
}
